"use client";

import type { Table } from "@tanstack/react-table";
import type * as React from "react";
import { useTranslation } from "react-i18next";

import { DataTableViewOptions } from "@/components/data-table/data-table-view-options";
import { DataTableExport } from "@/components/data-table/data-table-export";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { RefreshCw, Upload } from "lucide-react";

interface DataTableAdvancedToolbarProps<TData>
  extends React.ComponentProps<"div"> {
  table: Table<TData>;
  onRefresh?: () => void;
  isLoading?: boolean;
  onImport?: () => void;
  onExport?: () => void;
}

export function DataTableAdvancedToolbar<TData>({
  table,
  isLoading,
  className,
  onRefresh,
  onImport,
  onExport,
  ...props
}: DataTableAdvancedToolbarProps<TData>) {
  const { t } = useTranslation("common");
  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      className={cn(
        "flex ml-auto items-start justify-end gap-2",
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-2">
        {
          onRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4" />
              {t("dataTable.refresh")}
            </Button>
          )
        }
        <DataTableExport
          table={table}
          isLoading={isLoading}
          onExport={onExport}
        />
        {
          // TODO: Add import functionality
          onImport && (
            <Button variant="outline" size="sm" onClick={onImport}>
              <Upload className="h-4 w-4" />
              {t("dataTable.import")}
            </Button>
          )
        }
        <DataTableViewOptions table={table} isLoading={isLoading} />
      </div>
    </div>
  );
}
